import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, XCircle, Crown } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [planName, setPlanName] = useState("Pro");
  const { setUser, API } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { navigate("/app"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
          setUser(me.data);
          setPlanName(me.data.plan === "amateur" ? "Amateur" : "Pro");
          setStatus("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch { /* keep polling */ }
      if (attempts < 15) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, []); // eslint-disable-line

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-slate-50 flex items-center justify-center px-4">
      <div className="text-center max-w-sm" data-testid="payment-result">
        {status === "checking" && (
          <>
            <div className="inline-block h-10 w-10 border-2 border-[#FFD700] border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-slate-400">Confirming your payment...</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle size={56} weight="fill" className="text-[#00E676] mx-auto mb-4" />
            <h1 className="font-display text-4xl font-black uppercase tracking-tight mb-2 flex items-center justify-center gap-2">
              You're {planName} <Crown size={28} weight="fill" className="text-[#FFD700]" />
            </h1>
            <p className="text-sm text-slate-400 mb-6">
              {planName === "Pro"
                ? "Unlimited builds, all 4 sports, EV numbers, and daily Best Value picks are unlocked."
                : "5 builds a day, all 4 sports, and full EV + confidence numbers are unlocked."}
            </p>
            <Link to="/app" data-testid="back-to-builder-btn"
              className="rounded-full bg-[#00E676] text-black font-bold px-8 py-3 text-sm hover:scale-105 transition-transform inline-block">
              Back to the builder
            </Link>
          </>
        )}
        {(status === "failed" || status === "timeout") && (
          <>
            <XCircle size={56} weight="fill" className="text-[#FF3366] mx-auto mb-4" />
            <p className="text-slate-400 mb-6">{status === "failed" ? "Payment didn't go through." : "Still processing — check back in a minute."}</p>
            <Link to="/app" className="rounded-full bg-white/10 font-bold px-8 py-3 text-sm hover:bg-white/20 transition-colors inline-block">
              Back to the builder
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-slate-50 flex items-center justify-center px-4">
      <div className="text-center" data-testid="payment-cancel">
        <p className="text-slate-400 mb-6">Checkout cancelled — no charge was made.</p>
        <Link to="/app" className="rounded-full bg-white/10 font-bold px-8 py-3 text-sm hover:bg-white/20 transition-colors inline-block">
          Back to the builder
        </Link>
      </div>
    </div>
  );
}
